import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';

import { useTranslator } from '@/hooks/useTranslator';

interface Props {
  readonly totalCount: number;
  readonly currentCount: number;

  onSubmit(): void;
}

const TWO_PI = 2 * Math.PI;
const K = 30 * TWO_PI;

export const StepperProgressWidget: React.FC<Props> = (props: Props): React.ReactElement => {
  const { totalCount, currentCount, onSubmit } = props;
  const { palette } = useTheme();

  if (totalCount === 0) {
    throw new Error('Total count cannot be 0');
  }

  const percentage = React.useMemo(
    (): number => Math.floor(Math.min((100 * currentCount) / totalCount, 100)),
    [currentCount, totalCount],
  );

  const tr = useTranslator();
  const dash = React.useMemo((): number => (percentage * K) / 100, [percentage]);
  const offset = React.useMemo((): number => -45 * Math.PI, []);
  const gap = React.useMemo((): number => K - dash, [dash]);

  return (
    <Paper role="progress" elevation={0}>
      <Box padding={3} position="relative">
        <Box display="flex" alignItems="center" mb={3} gap={2}>
          <Box sx={styles.percentagePie}>
            <svg
              width="100%"
              height="100%"
              viewBox="0 0 72 72"
              fill="none"
              xmlns="http://www.w3.org/2000/svg">
              <circle cx={36} cy={36} r={30} stroke={palette.custom.DividerLight} strokeWidth={6} />
              <circle
                cx={36}
                cy={36}
                r={30}
                stroke={palette.primary.main}
                strokeWidth={6}
                strokeDashoffset={offset}
                strokeDasharray={`${dash} ${gap}`}
              />
              <text
                x={36}
                y={36}
                dominantBaseline="middle"
                textAnchor="middle"
                style={{ fontFamily: 'Lexend' }}
                fontWeight={500}
                fill={palette.custom.Inactive}>
                {percentage}%
              </text>
            </svg>
          </Box>
          <Box>
            <Typography variant="subtitle1">
              {tr('COUNT_OF_COUNT_COMPLETE', String(currentCount), String(totalCount))}
            </Typography>
            <Typography variant="body1" color="secondary">
              {tr('FILL_ALL_ENTRIES_AND_SUBMIT')}
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          fullWidth={true}
          disabled={currentCount < totalCount}
          onClick={onSubmit}>
          {tr('REVIEW_AND_SUBMIT')}
        </Button>
      </Box>
    </Paper>
  );
};

const styles: Record<string, SystemStyleObject> = {
  percentagePie: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: 72,
    height: 72,
  },
};
